/*global Autodesk*/
/// <reference types="forge-viewer" />
/// <reference types="THREE" />
import mitt, { Emitter, EventType } from "mitt";
/* WARNING: trying to import THREE from "three" will result in a different version of THREE being imported
 * than what the Autodesk Viewer is using. This will cause the viewer to not work properly.
 * Instead, use the <reference types="THREE" /> directive to import the correct version of THREE.
 * And add THREE to globals in .eslintrc.js.
 *
 * import * as THREE from "three";
 */

const config3d: Autodesk.Viewing.InitializerOptions = {
  env: "AutodeskProduction2",
  api: "streamingV2", // for models uploaded to EMEA change this option to 'streamingV2_EU'
};

export interface ModelCategory {
  category: string;
  categoryId: string;
  dbIds: number[];
  externalIds: string[];
}

/**
 * ModelElements is a mapping of CAD Ids to dbIds plus per-CAD attribute info
 */
export interface ModelElements {
  dbIds: Record<string, number>;
  /**
   * attributes[cadId] -> { system?: string, subSystem?: string, freeText1?: string, freeText2?: string, freeText3?: string }
   */
  attributes?: Record<string, { system?: string; subSystem?: string; freeText1?: string; freeText2?: string; freeText3?: string }>;
}

export interface ProgressUpdateEvent {
  percent: number;
  state: number; // Autodesk.Viewing.ProgressState [ROOT_LOADED, LOADING, RENDERING]
  model: Autodesk.Viewing.Model;
}

export interface GeometryLoadedEvent {
  type: string;
  model: Autodesk.Viewing.Model;
  target: Autodesk.Viewing.GuiViewer3D;
}

export interface ModelAddedEvent {
  type: string;
  model: Autodesk.Viewing.Model;
  target: Autodesk.Viewing.GuiViewer3D;
}

export interface ModelUnloadedEvent {
  type: string;
  model: Autodesk.Viewing.Model;
  target: Autodesk.Viewing.GuiViewer3D;
}

export interface SelectionChangedEvent {
  type: string;
  fragIdsArray: number[];
  dbIdArray: number[];
  nodeArray: number[];
  model: Autodesk.Viewing.Model;
  mouseButton: number;
  target: Autodesk.Viewing.GuiViewer3D;
}

export interface AggregateSelection {
  dbIdArray: number[];
  fragIdsArray: number[];
  model: Autodesk.Viewing.Model;
  nodeArray: number[];
}

export interface AggregateIsolation {
  ids: number[];
  model: Autodesk.Viewing.Model;
}

export interface AggregateSelectionChangedEvent {
  type: string;
  selections: AggregateSelection[];
  mouseButton: number;
  target: Autodesk.Viewing.GuiViewer3D;
}

export interface AggregateIsolationChangedEvent {
  type: string;
  isolation: AggregateIsolation[];
  target: Autodesk.Viewing.GuiViewer3D;
}

export interface ShowPropertiesEvent {
  type: string;
  dbId: number;
  model: Autodesk.Viewing.Model;
}

export interface OnInit {
  callback?: () => void;
  extensions: string[];
  memoryLimitedEnabled?: boolean;
  memoryLimitedDebugEnabled?: boolean;
}

export class LmViewer3D {
  _view?: Autodesk.Viewing.AggregatedView;
  _doc?: Autodesk.Viewing.Document;
  _urns: string[] = [];
  _urnsQueue: string[] = [];
  _emitter = mitt(); // TODO: can use built-in viewer dispatcher too
  _preventDefault = false;
  _isInitialized = false;

  get emitter(): Emitter<Record<EventType, unknown>> {
    return this._emitter;
  }

  get viewer(): Autodesk.Viewing.GuiViewer3D {
    return this._view?.viewer as Autodesk.Viewing.GuiViewer3D;
  }

  get doc(): Autodesk.Viewing.Document {
    return this._doc!;
  }

  get urns(): string[] {
    return this._urns;
  }

  get id(): string {
    return this._id;
  }

  constructor(
    private _id: string,
    private _options: Autodesk.Viewing.InitializerOptions = {},
  ) {
    console.log(`[${this._id}] Creating Large Model Viewer...`);
  }

  public destroy = () => {
    console.log(`[${this._id}] Destroying Large Model Viewer ...`);
    try {
      this.viewer?.removeEventListener(Autodesk.Viewing.GEOMETRY_LOADED_EVENT, this.onGeometryLoaded);
      this.viewer?.removeEventListener(Autodesk.Viewing.MODEL_ADDED_EVENT, this.onModelAdded);

      this.viewer?.removeEventListener(Autodesk.Viewing.SELECTION_CHANGED_EVENT, this.onSelectionChanged);
      this.viewer?.removeEventListener(Autodesk.Viewing.AGGREGATE_SELECTION_CHANGED_EVENT, this.onAggregateSelectionChanged);
      this.viewer?.removeEventListener(Autodesk.Viewing.AGGREGATE_ISOLATION_CHANGED_EVENT, this.onAggregateIsolationChanged);

      const view = this._view;
      this._view = undefined;
      // @ts-expect-error: Property 'destroy' does not exist on type 'AggregatedView'.
      view?.destroy();
      console.log(`[${this._id}] Destroying Large Model Viewer ... Done`);
    } catch (e) {
      console.log(e);
    }
  };

  public initialize = (extensions: string[] = [], callback?: () => void) => {
    console.log(`[${this._id}] Initializing Large Model Viewer...`);

    if (typeof Autodesk === "undefined") {
      throw new Error("Autodesk undefined");
    }

    extensions.push("Autodesk.Viewing.MemoryLimitedDebug");

    const options: Autodesk.Viewing.InitializerOptions = { ...config3d, ...this._options };
    Autodesk.Viewing.Initializer(options, this.onInit.bind(this, { callback, extensions, memoryLimitedEnabled: true, memoryLimitedDebugEnabled: true }));
  };

  private onInit = async ({ callback, extensions, memoryLimitedEnabled, memoryLimitedDebugEnabled }: OnInit) => {
    console.log(`[${this._id}] onInit Large Model Viewer...`);

    const htmlDiv = document.getElementById(this._id);
    if (!htmlDiv) {
      console.error(`[${this._id}] Failed to find HTML element with id: ${this._id}`);
      return;
    }

    const options: any = {
      id: this._id,
      emitter: this._emitter,
      searchAncestors: true,
    };

    if (memoryLimitedEnabled) {
      options.loaderExtensions = {
        svf: "Autodesk.MemoryLimited",
        rvt: "Autodesk.MemoryLimited", // maybe?
      };
      options.memory = {
        limit: 1024,
        debug: {
          force: memoryLimitedDebugEnabled,
        },
      };
    }

    options.extensions = (extensions || []).map((ext: string) => {
      return {
        name: ext,
        getOptions: () => options
      };
    });
    this._view = new Autodesk.Viewing.AggregatedView();
    this._view.init(htmlDiv, options);

    const child = htmlDiv.firstChild as HTMLElement;
    if (child) {
      child.id = this._id + "-viewer";
    }

    if (memoryLimitedDebugEnabled) {
      this.viewer.loadExtension("Autodesk.Viewing.MemoryLimitedDebug");
    }

    const startedCode = this.viewer.start();
    if (startedCode > 0) {
      console.error(`[${this._id}] Failed to create a Viewer: WebGL not supported.`);
      return;
    }

    this.viewer.addEventListener(Autodesk.Viewing.GEOMETRY_LOADED_EVENT, this.onGeometryLoaded.bind(this));
    this.viewer.addEventListener(Autodesk.Viewing.MODEL_ADDED_EVENT, this.onModelAdded.bind(this));
    this.viewer.addEventListener(Autodesk.Viewing.MODEL_UNLOADED_EVENT, this.onModelUnloaded.bind(this));

    this.viewer.addEventListener(Autodesk.Viewing.SELECTION_CHANGED_EVENT, this.onSelectionChanged.bind(this));
    this.viewer.addEventListener(Autodesk.Viewing.AGGREGATE_SELECTION_CHANGED_EVENT, this.onAggregateSelectionChanged.bind(this));
    this.viewer.addEventListener(Autodesk.Viewing.AGGREGATE_ISOLATION_CHANGED_EVENT, this.onAggregateIsolationChanged.bind(this));

    this.viewer.addEventListener(Autodesk.Viewing.ISOLATE_EVENT, this.onIsolate.bind(this));
    this.viewer.addEventListener(Autodesk.Viewing.SHOW_ALL_EVENT, this.onShowAll.bind(this));
    this.viewer.addEventListener(Autodesk.Viewing.SHOW_EVENT, this.onShow.bind(this));
    this.viewer.addEventListener(Autodesk.Viewing.HIDE_ALL_EVENT, this.onHideAll.bind(this));
    this.viewer.addEventListener(Autodesk.Viewing.HIDE_EVENT, this.onHide.bind(this));

    this.viewer.addEventListener("showProperties", this.onShowProperties.bind(this));

    console.log(`[${this._id}] Initialization complete, loading a model next...`);

    this._isInitialized = true;

    while (this._urnsQueue.length > 0) {
      const urn = this._urnsQueue.shift();
      if (urn) {
        await this.loadDocument(urn);
      }
    }

    if (callback) {
      callback();
    }
  };

  public setProfile_Default = () => {
    const customProfileSettings = {
      settings: {
        reverseMouseZoomDir: true, // override existing
        reverseHorizontalLookDirection: true, // override existing
        openPropertiesOnSelect: true,
      },
      extensions: {
        unload: [],
      },
    };
    const customProfile = new (<any>Autodesk.Viewing).Profile(customProfileSettings);
    this.viewer?.setProfile(customProfile);
  };

  /**
   * Sets the viewer profile to AEC (Architecture, Engineering, and Construction).
   * This method configures the viewer with settings specific to AEC workflows.
   * It utilizes the Autodesk.Viewing.ProfileSettings.AEC and applies it to the viewer.
   *
   * @returns {void}
   */
  public setProfile_AEC = () => {
    const profileSettings = (<any>Autodesk.Viewing).ProfileSettings.AEC;
    const profile = new (<any>Autodesk.Viewing).Profile(profileSettings);
    this.viewer?.setProfile(profile);
  };

  /**
   * Sets the viewer profile to Navis using Autodesk Viewing's ProfileSettings.
   * This method creates a new profile based on the Navis profile settings and applies it to the viewer.
   *
   * @returns {void}
   */
  public setProfile_Navis = () => {
    const profileSettings = (<any>Autodesk.Viewing).ProfileSettings.Navis;
    const profile = new (<any>Autodesk.Viewing).Profile(profileSettings);
    this.viewer?.setProfile(profile);
  };

  public loadDocument = async (urn: string): Promise<void> => {
    return new Promise<void>((resolve, reject) => {
      if (!this._urns.includes(urn)) {
        console.log(`[${this._id}] load urn: ${urn}`);

        if (!this._isInitialized) {
          this._urnsQueue.push(urn);
          resolve();
          return;
        }

        this._urns.push(urn);
        const paramUrn = !urn.startsWith("urn:") ? "urn:" + urn : urn;

        Autodesk.Viewing.Document.load(paramUrn,
          async (doc) => {
            await this.onLoadSuccess(doc);
            resolve();
          },
          (errorCode: number) => {
            this.onLoadError(errorCode);
            reject();
          });
      } else {
        resolve();
      }
    });
  };

  private RemoveUrn = (urn: string) => {
    const index1 = this._urns.indexOf(urn);
    if (index1 !== -1) {
      this._urns.splice(index1, 1);
    }

    const index2 = this._urnsQueue.indexOf(urn);
    if (index2 !== -1) {
      this._urnsQueue.splice(index2, 1);
    }
  };

  private RemoveAllUrns = () => {
    this._urns = [];
    this._urnsQueue = [];
  };

  public unloadDocument = async (urn: string): Promise<void> => {
    console.log(`[${this._id}] unload urn: ${urn}`);

    const models = this.viewer?.impl.modelQueue().getAllModels() || [];
    const model = models.find((model: Autodesk.Viewing.Model) => urn.includes(model.myData.urn));
    if (!model) {
      throw new Error(`${urn} not found`);
    }

    /* AggregateView unload */
    await this._view?.unload(model.getDocumentNode());

    this.RemoveUrn(urn);

    if (this._urns.length === 0) {
      this._emitter.emit(Autodesk.Viewing.UNLOAD_ALL_DOCUMENTS_EVENT);
    }
  };

  public unloadAllDocuments = async (): Promise<void> => {
    console.log(`[${this._id}] unload all documents`);

    const models = this.viewer?.impl.modelQueue().getAllModels() || [];
    models.forEach((model: Autodesk.Viewing.Model) => {
      this.viewer?.impl.unloadModel(model);
    });

    this.RemoveAllUrns();

    this._emitter.emit(Autodesk.Viewing.UNLOAD_ALL_DOCUMENTS_EVENT);
  };

  private onLoadSuccess = async (doc: any) => {
    console.log(`[${this._id}] Autodesk.Viewing.Document loaded: `, doc);

    this._doc = doc;

    await doc.downloadAecModelData();

    const filterGeom = (geom: any) => ["3d"].indexOf(geom.data.role) !== -1;
    const geometries = this.doc.getRoot().search({ type: "geometry" }).filter(filterGeom);
    console.log(`[${this._id}] geometries: `, geometries);

    let viewable = this.doc.getRoot().getDefaultGeometry();
    if (geometries?.length > 0) {
      const guid = geometries[0].data.guid;
      viewable = this.doc.getRoot().findByGuid(guid);
    }

    /* AggregateView load */
    const options = {
      applyScaling: 'm',
      applyRefPoint: false,
      preserveView: false,
    };

    if (!this._view?.isEmpty()) {
      options.preserveView = true;
    }

    this._view?.fetchAlignments([this.doc.myData.urn]);

    await this._view?.show(viewable, options);
  };

  private onLoadError = async (errorCode: number) => {
    const errorMessage = errorCodes[errorCode];
    console.log(`[${this._id}] Autodesk.Viewing.Document Error ${errorCode}: ${errorMessage}`);
  };

  private onProgressUpdate = (event: ProgressUpdateEvent): Promise<void> => {
    return new Promise<void>((resolve) => {
      console.log(`[${this._id}] onProgressUpdate: `, { state: event.state, percent: event.percent, modelId: event.model.id, loadDone: event.model.getData().loadDone });
      resolve();
    });
  };

  private onGeometryLoaded = async (event: GeometryLoadedEvent): Promise<void> => {
    console.log(`[${this._id}] onGeometryLoaded: `, event);

    this.viewer?.fitToView();

    const model = event.model;

    // Build model categories and model elements including attributes
    const modelCategories = await this.getModelCategories(model);
    // @ts-ignore __modelCategories is a custom property
    model.__modelCategories = modelCategories;

    const modelElements = await this.getModelElements(model);
    // @ts-ignore __modelElements is a custom property
    model.__modelElements = modelElements;

    this._emitter.emit(Autodesk.Viewing.GEOMETRY_LOADED_EVENT, event);
  };

  private onModelAdded = async (event: ModelAddedEvent): Promise<void> => {
    console.log(`[${this._id}] onModelAdded: `, event);
    this._emitter.emit(Autodesk.Viewing.MODEL_ADDED_EVENT, event);
  };

  private onModelUnloaded = async (event: ModelUnloadedEvent): Promise<void> => {
    console.log(`[${this._id}] onModelUnloaded: `, event);
    this._emitter.emit(Autodesk.Viewing.MODEL_UNLOADED_EVENT, event);
  };

  public logProperties = (model: Autodesk.Viewing.Model, dbIds: number[] = []): Promise<void> => {
    return new Promise<void>((resolve) => {
      model.getBulkProperties(dbIds, {}, (_propResults: Autodesk.Viewing.PropertyResult[]) => {
        resolve();
      });
    });
  };

  public getAggregateSelections = (): AggregateSelection[] => {
    return this.viewer?.getAggregateSelection()
      .map((selection: any) => ({
        dbIdArray: selection.selection,
        model: selection.model
      } as AggregateSelection));
  };

  private getModelCategories = (model: Autodesk.Viewing.Model): Promise<ModelCategory[]> => {
    return new Promise<ModelCategory[]>((resolve) => {
      let propFilter = { propFilter: ["externalId", "_RC", "Name", "type"] };

      const categories: ModelCategory[] = [];

      model.getBulkProperties([], propFilter, (propertyResults: Autodesk.Viewing.PropertyResult[]) => {
        propertyResults.forEach((propResult) => {
          propResult.properties.forEach((p: any) => {
            let category: string | undefined;

            if (p.displayName === "_RC" && p.displayCategory === "__category__") {
              category = p.displayValue as string;
            }
            if (p.displayName === "type") {
              category = p.displayValue as string;
            }

            if (category) {
              let item = categories.find((r) => r.category === category);

              if (!item) {
                item = {
                  category,
                  categoryId: "",
                  dbIds: [],
                  externalIds: [],
                };
                categories.push(item);
              }
              item.dbIds.push(propResult.dbId);
              if (propResult.externalId) {
                item.externalIds.push(propResult.externalId);
              }
            }
          });
        });

        propFilter = { propFilter: ["Category", "CategoryId"] };

        model.getBulkProperties([], propFilter, (propertyResults: Autodesk.Viewing.PropertyResult[]) => {
          propertyResults.forEach((propResult) => {
            let categoryId: string | undefined;
            propResult.properties.every((p: any) => {
              if (p.displayCategory === "__categoryId__") {
                categoryId = p.displayValue.toString();
              }
              return !categoryId;
            });

            if (categoryId) {
              propResult.properties.forEach((p: any) => {
                if (p.displayCategory === "__category__") {
                  let category = p.displayValue as string;
                  if (category.startsWith("Revit ")) {
                    category = category.substring(6);
                  }
                  const item = categories.find((r) => r.category === category);
                  if (item) {
                    item.categoryId = categoryId!;
                  }
                  return false;
                }
                return true;
              });
            }
          });

          resolve(categories);
        });
      });
    });
  };

  /**
   * Build model.__modelElements with:
   *  - dbIds: { cadId -> dbId }
   *  - attributes: { cadId -> { system, subSystem, freeText1, freeText2, freeText3 } }
   *
   * This attempts to read common property names in a robust way (different exporters use different casing).
   */
  private getModelElements = (model: Autodesk.Viewing.Model): Promise<ModelElements> => {
    return new Promise<ModelElements>((resolve) => {
      // Request properties we care about (ElementId / elementId) and any custom attributes
      const propFilter = ["ElementId", "elementId", "System", "system", "SubSystem", "subsystem", "FreeText1", "FreeText2", "FreeText3", "FreeText_1", "FreeText_2", "FreeText_3"];

      model.getBulkProperties([], { propFilter }, (propResults: Autodesk.Viewing.PropertyResult[]) => {
        const modelElements: ModelElements = { dbIds: {}, attributes: {} };

        propResults.forEach((propResult) => {
          // determine cadId (external element id)
          let cadId: string | undefined;

          // try to find ElementId/elementId property
          propResult.properties.forEach((p: any) => {
            const name = (p.displayName || "").toString();
            if (name.toLowerCase() === "elementid" || name.toLowerCase() === "element id" || name.toLowerCase() === "elementid") {
              cadId = p.displayValue?.toString();
            }
            if (!cadId && (p.attributeName === "elementId" || p.attributeName === "ElementId")) {
              cadId = p.displayValue?.toString();
            }
          });

          // fallback to externalId
          if (!cadId && propResult.externalId) {
            cadId = propResult.externalId;
          }

          if (!cadId) {
            return; // can't map
          }

          // store dbId mapping
          modelElements.dbIds[cadId] = propResult.dbId;

          // parse attributes from properties for this cadId
          const attr: any = {};
          propResult.properties.forEach((p: any) => {
            const propName = (p.displayName || p.displayCategory || p.attributeName || "").toString().toLowerCase();

            // detect System / SubSystem / FreeText1/2/3 by common names and fallbacks
            if (propName === "system" || propName === "systemname" || propName === "system id" || propName === "systemid") {
              attr.system = p.displayValue?.toString();
            } else if (propName === "subsystem" || propName === "sub-system" || propName === "sub system" || propName === "subsystemname") {
              attr.subSystem = p.displayValue?.toString();
            } else if (propName === "freetext1" || propName === "free text 1" || propName === "free text1" || propName === "freetext_1") {
              attr.freeText1 = p.displayValue?.toString();
            } else if (propName === "freetext2" || propName === "free text 2" || propName === "freetext_2") {
              attr.freeText2 = p.displayValue?.toString();
            } else if (propName === "freetext3" || propName === "free text 3" || propName === "freetext_3") {
              attr.freeText3 = p.displayValue?.toString();
            } else {
              // also inspect known attribute categories: sometimes the property is reported with displayCategory indicating attribute key
              if (p.displayCategory && typeof p.displayCategory === "string") {
                const cat = p.displayCategory.toLowerCase();
                if (cat === "system") attr.system = attr.system || p.displayValue?.toString();
                if (cat === "subsystem" || cat === "sub-system") attr.subSystem = attr.subSystem || p.displayValue?.toString();
              }
            }
          });

          if (Object.keys(attr).length > 0) {
            modelElements.attributes![cadId] = {
              system: attr.system,
              subSystem: attr.subSystem,
              freeText1: attr.freeText1,
              freeText2: attr.freeText2,
              freeText3: attr.freeText3,
            };
          }
        });

        // If nothing found by property query, attempt external id mapping fallback
        if (Object.keys(modelElements.dbIds).length === 0) {
          model.getExternalIdMapping(
            (dbIds) => {
              modelElements.dbIds = dbIds;
              resolve(modelElements);
            },
            () => {
              resolve(modelElements);
            },
          );
        } else {
        resolve(modelElements);
        }
      });
    });
  };

  private onSelectionChanged = async (event: SelectionChangedEvent): Promise<void> => {
    if (this._preventDefault) {
      return;
    }
    console.log(`[${this._id}] onSelectionChanged: `, event);
    this._emitter.emit(Autodesk.Viewing.SELECTION_CHANGED_EVENT, event);
  };

  private onAggregateSelectionChanged = async (event: AggregateSelectionChangedEvent): Promise<void> => {
    if (this._preventDefault) {
      return;
    }
    console.log(`[${this._id}] onAggregateSelectionChanged: `, event);
    this._emitter.emit(Autodesk.Viewing.AGGREGATE_SELECTION_CHANGED_EVENT, event);
  };

  private onAggregateIsolationChanged = async (event: any): Promise<void> => {
    if (this._preventDefault) {
      return;
    }
    console.log(`[${this._id}] onAggregateIsolationChanged: `, event);
    this._emitter.emit(Autodesk.Viewing.AGGREGATE_ISOLATION_CHANGED_EVENT, event);
  };

  private onIsolate = async (event: any): Promise<void> => {
    console.log(`[${this._id}] onIsolate: `, event);
    this._emitter.emit(Autodesk.Viewing.ISOLATE_EVENT, event);
  };

  private onShowAll = async (event: any): Promise<void> => {
    console.log(`[${this._id}] onShowAll: `, event);
    this._emitter.emit(Autodesk.Viewing.SHOW_ALL_EVENT, event);
  };

  private onShow = async (event: any): Promise<void> => {
    console.log(`[${this._id}] onShow: `, event);
    this._emitter.emit(Autodesk.Viewing.SHOW_EVENT, event);
  };

  private onHideAll = async (event: any): Promise<void> => {
    console.log(`[${this._id}] onHideAll: `, event);
    this._emitter.emit(Autodesk.Viewing.HIDE_ALL_EVENT, event);
  };

  private onHide = async (event: any): Promise<void> => {
    console.log(`[${this._id}] onHide: `, event);
    this._emitter.emit(Autodesk.Viewing.HIDE_EVENT, event);
  };

  private onShowProperties = async (_event: any): Promise<void> => {
    // no-op (kept for compatibility)
  };

  /**
   * Isolates the specified CAD IDs by turning off the visibility of all other elements.
   */
  public isolateByCadIds = async (cadIds: string[], urn?: string | undefined): Promise<void> => {
    console.log(`[${this._id}] Isolating cadIds: `, cadIds, urn);
    this.turnOffAllVisibility(urn);
    await this.toggleVisibilityByCadIds(cadIds, urn, true);
  };

  

  /**
   * Select the specified CAD IDs by unselecting all other elements.
   */
  public selectByCadIds = async (cadIds: string[], urn?: string | undefined): Promise<void> => {
    console.log(`[${this._id}] Select cadIds: `, cadIds, urn);
    this._preventDefault = true;
    this.viewer?.impl
      .modelQueue()
      .getAllModels()
      .forEach((model: any) => {
        if (!urn || urn === model.myData.urn) {
          const modelElements: any = model.__modelElements as ModelElements;
          if (modelElements) {
            const dbIds = cadIds?.filter((cadId: string) => modelElements.dbIds[cadId]).map((cadId: string) => +modelElements.dbIds[cadId]) || [];
            this.viewer?.select(dbIds, model);
          }
        }
      });
    this._preventDefault = false;
  };

  /**
   * Helper: get all cadIds for a given system GUID across loaded models (optional urn filter).
   */
  public getCadIdsBySystem = async (systemGuid: string | string[], urn?: string | undefined): Promise<string[]> => {
    if (!systemGuid || (Array.isArray(systemGuid) && systemGuid.length === 0)) return [];

    const guids = Array.isArray(systemGuid) ? systemGuid.map(String) : [String(systemGuid)];
    const results = new Set<string>();

    const models = this.viewer?.impl.modelQueue().getAllModels() || [];
    for (const model of models) {
      if (urn && urn !== model.myData.urn) continue;
      // @ts-ignore __modelElements is custom
      const modelElements: ModelElements = model.__modelElements as ModelElements;
      if (!modelElements || !modelElements.attributes) continue;

      Object.entries(modelElements.attributes).forEach(([cadId, attrs]) => {
        if (!attrs) return;
        if (attrs.system && guids.includes(String(attrs.system))) {
          results.add(cadId);
            }
          });
        }

    return Array.from(results);
  };

  /**
   * Helper: get all cadIds for a given subSystem GUID across loaded models (optional urn filter).
   */
  public getCadIdsBySubSystem = async (subSystemGuid: string | string[], urn?: string | undefined): Promise<string[]> => {
    if (!subSystemGuid || (Array.isArray(subSystemGuid) && subSystemGuid.length === 0)) return [];

    const guids = Array.isArray(subSystemGuid) ? subSystemGuid.map(String) : [String(subSystemGuid)];
    const results = new Set<string>();

    const models = this.viewer?.impl.modelQueue().getAllModels() || [];
    for (const model of models) {
      if (urn && urn !== model.myData.urn) continue;
      const modelElements: ModelElements = model.__modelElements as ModelElements;
      if (!modelElements || !modelElements.attributes) continue;

      Object.entries(modelElements.attributes).forEach(([cadId, attrs]) => {
        if (!attrs) return;
        if (attrs.subSystem && guids.includes(String(attrs.subSystem))) {
          results.add(cadId);
        }
      });
    }

    return Array.from(results);
  };

  
  /**
   * Helper: get cadIds by free-text attribute (case-sensitive match by default).
   * attributeName must be one of 'freeText1'|'freeText2'|'freeText3' or variants.
   */
  public getCadIdsByAttribute = async (attributeName: string, value: string, urn?: string | undefined): Promise<string[]> => {
    if (!attributeName || !value) return [];

    const attrKey = attributeName.replace(/\s+/g, "").toLowerCase(); // normalize
    const results = new Set<string>();

    const models = this.viewer?.impl.modelQueue().getAllModels() || [];
    for (const model of models) {
      if (urn && urn !== model.myData.urn) continue;
      const modelElements: ModelElements = model.__modelElements as ModelElements;
      if (!modelElements || !modelElements.attributes) continue;

      Object.entries(modelElements.attributes).forEach(([cadId, attrs]) => {
        if (!attrs) return;
        const candidate = (attrs as any)[attrKey] ?? (attrs as any)[attributeName];
        if (candidate && String(candidate) === String(value)) {
          results.add(cadId);
        }
      });
    }

    return Array.from(results);
  };

  /**
   * Isolate convenience: System GUIDs -> cadIds -> isolate
   */
  public isolateBySystemGuid = async (systemGuid: string | string[] | undefined, urn?: string | undefined): Promise<void> => {
    if (!systemGuid) {
      await this.resetView();
      return;
    }
    const cadIds = await this.getCadIdsBySystem(systemGuid, urn);
    if (cadIds.length === 0) {
      console.warn(`[${this._id}] isolateBySystemGuid: no cadIds found for ${systemGuid}`);
      await this.resetView();
      return;
    }
    await this.isolateByCadIds(cadIds, urn);
  };

  /* 
   * @param categoryId - The category of model elements to isolate.
   * @returns A promise that resolves when the isolation process is complete.
   */
  public isolateByModelCategoryId = async (categoryId: string | undefined): Promise<void> => {
    console.log(`[${this._id}] isolateByModelCategoryKey categoryKey '${categoryId}'`);

    if (!categoryId) {
      return;
    }

    this.viewer?.impl
      .modelQueue()
      .getAllModels()
      .forEach((model: any) => {
        let dbIds: number[] = [];

        if (model.__modelCategories) {
          model.visibilityManager.setAllVisibility(false);

          // TODO: convert this to a map based on modelCategory.key
          // after the modelCategory.keys are set in the getModelCategories method
          model.__modelCategories?.forEach((modelCategory: ModelCategory) => {
            if (modelCategory.categoryId === categoryId) {
              (async () => {
                dbIds = dbIds.concat(await this.getLeafNodes(model, modelCategory.dbIds));
              })();
              dbIds = dbIds.concat(modelCategory.dbIds);
            }
          });
        }

        //console.log(`[${this._id}] Isolating dbIds: `, dbIds);

        if (dbIds.length > 0) {
          this.viewer?.isolate(dbIds, model);
          this.viewer?.fitToView(dbIds, model, false);
        } else {
          console.log(`[${this._id}] Category not found '${categoryId}'`);
        }
      });
  };

  /**
   * Isolate convenience: SubSystem GUIDs -> cadIds -> isolate
   */
  public isolateBySubSystemGuid = async (subSystemGuid: string | string[] | undefined, urn?: string | undefined): Promise<void> => {
    if (!subSystemGuid) {
      await this.resetView();
      return;
    }
    const cadIds = await this.getCadIdsBySubSystem(subSystemGuid, urn);
    if (cadIds.length === 0) {
      console.warn(`[${this._id}] isolateBySubSystemGuid: no cadIds found for ${subSystemGuid}`);
      await this.resetView();
      return;
    }
    await this.isolateByCadIds(cadIds, urn);
  };

  /**
   * Isolate convenience: Attribute name/value -> cadIds -> isolate
   */
  public isolateByAttribute = async (attributeName: string, value: string | undefined, urn?: string | undefined): Promise<void> => {
    if (!value) {
      await this.resetView();
      return;
    }
    const cadIds = await this.getCadIdsByAttribute(attributeName, value, urn);
    if (cadIds.length === 0) {
      console.warn(`[${this._id}] isolateByAttribute: no cadIds found for ${attributeName}=${value}`);
      await this.resetView();
      return;
    }
    await this.isolateByCadIds(cadIds, urn);
  };

  /**
   * Select convenience: System GUIDs -> cadIds -> select
   */
  public selectBySystemGuid = async (systemGuid: string | string[] | undefined, urn?: string | undefined): Promise<void> => {
    if (!systemGuid) {
      return;
    }
    const cadIds = await this.getCadIdsBySystem(systemGuid, urn);
    if (cadIds.length === 0) {
      console.warn(`[${this._id}] selectBySystemGuid: no cadIds found for ${systemGuid}`);
      return;
    }
    await this.selectByCadIds(cadIds, urn);
  };

  /**
   * Select convenience: SubSystem GUIDs -> cadIds -> select
   */
  public selectBySubSystemGuid = async (subSystemGuid: string | string[] | undefined, urn?: string | undefined): Promise<void> => {
    if (!subSystemGuid) {
      return;
    }
    const cadIds = await this.getCadIdsBySubSystem(subSystemGuid, urn);
    if (cadIds.length === 0) {
      console.warn(`[${this._id}] selectBySubSystemGuid: no cadIds found for ${subSystemGuid}`);
      return;
    }
    await this.selectByCadIds(cadIds, urn);
  };

  /**
   * Select convenience: Attribute name/value -> cadIds -> select
   */
  public selectByAttribute = async (attributeName: string, value: string | undefined, urn?: string | string[] | undefined): Promise<void> => {
    if (!value) return;
    const cadIds = await this.getCadIdsByAttribute(attributeName, value, (urn as string | undefined) ?? undefined);
    if (cadIds.length === 0) {
      console.warn(`[${this._id}] selectByAttribute: no cadIds found for ${attributeName}=${value}`);
      return;
    }
    await this.selectByCadIds(cadIds, (urn as string | undefined) ?? undefined);
  };

  /**
   * Isolate convenience: System GUIDs -> cadIds -> isolate (for ByAttributes components)
   */
  public isolateBySystem = async (systemGuid: string | string[] | undefined, urn?: string | undefined): Promise<void> => {
    if (!systemGuid) {
      await this.resetView();
      return;
    }
    const cadIds = await this.getCadIdsBySystem(systemGuid, urn);
    if (cadIds.length === 0) {
      console.warn(`[${this._id}] isolateBySystem: no cadIds found for ${systemGuid}`);
      await this.resetView();
      return;
    }
    await this.isolateByCadIds(cadIds, urn);
  };

  /**
   * Isolate convenience: SubSystem GUIDs -> cadIds -> isolate (for ByAttributes components)
   */
  public isolateBySubSystem = async (subSystemGuid: string | string[] | undefined, urn?: string | undefined): Promise<void> => {
    if (!subSystemGuid) {
      await this.resetView();
      return;
    }
    const cadIds = await this.getCadIdsBySubSystem(subSystemGuid, urn);
    if (cadIds.length === 0) {
      console.warn(`[${this._id}] isolateBySubSystem: no cadIds found for ${subSystemGuid}`);
      await this.resetView();
      return;
    }
    await this.isolateByCadIds(cadIds, urn);
  };

  /**
   * Isolate convenience: Attribute value -> cadIds -> isolate (for ByAttributes components)
   */
  public isolateByAttributeValue = async (attributeName: string, value: string | undefined, urn?: string | undefined): Promise<void> => {
    if (!value) {
      await this.resetView();
      return;
    }
    const cadIds = await this.getCadIdsByAttribute(attributeName, value, urn);
    if (cadIds.length === 0) {
      console.warn(`[${this._id}] isolateByAttributeValue: no cadIds found for ${attributeName}=${value}`);
      await this.resetView();
      return;
    }
    await this.isolateByCadIds(cadIds, urn);
  };

  /**
   * Select convenience: System GUIDs -> cadIds -> select (for ByAttributes components)
   */
  public selectBySystem = async (systemGuid: string | string[] | undefined, urn?: string | undefined): Promise<void> => {
    if (!systemGuid) return;
    const cadIds = await this.getCadIdsBySystem(systemGuid, urn);
    if (cadIds.length === 0) {
      console.warn(`[${this._id}] selectBySystem: no cadIds found for ${systemGuid}`);
      return;
    }
    await this.selectByCadIds(cadIds, urn);
  };

  /**
   * Select convenience: SubSystem GUIDs -> cadIds -> select (for ByAttributes components)
   */
  public selectBySubSystem = async (subSystemGuid: string | string[] | undefined, urn?: string | undefined): Promise<void> => {
    if (!subSystemGuid) return;
    const cadIds = await this.getCadIdsBySubSystem(subSystemGuid, urn);
    if (cadIds.length === 0) {
      console.warn(`[${this._id}] selectBySubSystem: no cadIds found for ${subSystemGuid}`);
      return;
    }
    await this.selectByCadIds(cadIds, urn);
  };

  /**
   * Select convenience: Attribute value -> cadIds -> select (for ByAttributes components)
   */
  public selectByAttributeValue = async (attributeName: string, value: string | undefined, urn?: string | undefined): Promise<void> => {
    if (!value) return;
    const cadIds = await this.getCadIdsByAttribute(attributeName, value, urn);
    if (cadIds.length === 0) {
      console.warn(`[${this._id}] selectByAttributeValue: no cadIds found for ${attributeName}=${value}`);
      return;
    }
    await this.selectByCadIds(cadIds, urn);
  };

  public turnOnAllVisibility = async (urns?: string | string[] | undefined): Promise<void> => {
    console.log(`[${this._id}] turnOnAllVisibility: `, urns);

    if (!Array.isArray(urns)) {
      urns = urns ? [urns] : [];
    }

    this.viewer?.impl
      .modelQueue()
      .getAllModels()
      .forEach((model: any) => {
        if (urns.length === 0 || urns.includes(model.myData.urn)) {
          model.visibilityManager.setAllVisibility(true);
        }
      });
  };

  public turnOffAllVisibility = async (urns?: string | string[] | undefined): Promise<void> => {
    console.log(`[${this._id}] turnOffAllVisibility: `, urns);

    if (!Array.isArray(urns)) {
      urns = urns ? [urns] : [];
    }

    this.viewer?.impl
      .modelQueue()
      .getAllModels()
      .forEach((model: any) => {
        if (urns.length === 0 || urns.includes(model.myData.urn)) {
          model.visibilityManager.setAllVisibility(false);
        }
      });
  };

  public toggleVisibilityByCadIds = async (cadIds: string[], urn: string | undefined, visible: boolean): Promise<void> => {
    console.log(`[${this._id}] Toggling cadId visibility: `, cadIds, urn);
    this.viewer?.impl
      .modelQueue()
      .getAllModels()
      .forEach((model: any) => {
        if (!urn || urn === model.myData.urn) {
          const modelElements: any = model.__modelElements as ModelElements;
          if (modelElements) {
            const dbIds = cadIds?.filter((cadId: string) => modelElements.dbIds[cadId]).map((cadId: string) => +modelElements.dbIds[cadId]) || [];
            if (dbIds.length > 0) {
              if (visible) {
                this.viewer?.show(dbIds, model);
                const instanceTree = model.getInstanceTree();
                dbIds.forEach((Id: any) => {
                  instanceTree.enumNodeChildren(Id, function (dbId: any) {
                    const visLocked = instanceTree.isNodeVisibleLocked(dbId);

                    instanceTree.setNodeHidden(dbId, !visLocked);

                    instanceTree.enumNodeFragments(dbId, function (fragId: any) {
                      model.setVisibility(fragId, false);
                    }, false);
                  }, false);
                });
              } else {
                this.viewer?.hide(dbIds, model);
              }
            }
          }
        }
      });

    this.viewer?.fitToView();
  };

  public applyTheming = async (urn: string, elements: any): Promise<void> => {
    console.log(`[${this._id}] applyTheming: `, urn);
    try {
      const models = this.viewer?.impl.modelQueue().getAllModels();
      const model = models?.find((model: Autodesk.Viewing.Model) => urn === model.myData.urn);
      if (model) {
        console.log(`[${this._id}] applyTheming model: `, model);
        const modelElements: any = model.__modelElements as ModelElements;
        if (modelElements) {
          elements?.forEach((element: any) => {
            const dbId = modelElements.dbIds[element.cadId];
            if (dbId) {
              const rgb = new THREE.Color(element.hexColor);
              const color = new THREE.Vector4(rgb.r, rgb.g, rgb.b, 1.0);
              this.viewer?.setThemingColor(dbId, color, model, false);
            }
          });
        }
      }
      console.log(`[${this._id}] applyTheming done`);
    } catch (e) {
      console.log(e);
    }
  };

  public getLeafNodes = async (model: any, dbIds?: number[]): Promise<number[]> => {
    try {
      const instanceTree = model.getData().instanceTree || model.getFragmentMap();

      dbIds = dbIds || [instanceTree.getRootId()];

      const leafIds: number[] = [];

      const getLeafNodeIdsRec = (id: number) => {
        let childCount = 0;

        instanceTree.enumNodeChildren(id, (childId: number) => {
          getLeafNodeIdsRec(childId);
          ++childCount;
        });

        if (childCount === 0) {
          leafIds.push(id);
        }
      };

      dbIds?.forEach((dbId: number) => {
        getLeafNodeIdsRec(dbId);
      });

      return leafIds;
    } catch (e) {
      console.log(e);
      return [];
    }
  };

  public getCadIds = async (model: any, dbIds?: number[], recurse: boolean = false): Promise<string[]> => {
    const cadIds: string[] = [];
    try {
      const modelElements: any = model.__modelElements as ModelElements;
      if (modelElements) {
        if (recurse) {
          dbIds = await this.getLeafNodes(model, dbIds);
        }

        Object.entries(modelElements.dbIds).forEach(([cadId, dbId]) => {
          if (dbIds?.includes(dbId as number)) {
            cadIds.push(cadId);
          }
        });
      }
    } catch (e) {
      console.log(e);
    }

    return cadIds;
  };

  public getCadIdsFromSelections = async (selections: AggregateSelection[] | null | undefined): Promise<Map<string, Set<string>>> => {
    const getOrAddModel = (modelElements: Map<string, Set<string>>, modelId: string): Set<string> => {
      if (!modelElements.has(modelId)) {
        modelElements.set(modelId, new Set<string>());
      }
      return modelElements.get(modelId) as Set<string>;
    };

    const modelToCadIdMap = new Map<string, Set<string>>();

    try {
      for await (const selection of selections || []) {
        const model = selection.model;
        let dbIds = selection.dbIdArray;
        // @ts-ignore __modelElements is a custom property
        const modelElements: any = model.__modelElements as ModelElements;
        const cadIds = getOrAddModel(modelToCadIdMap, model.myData?.urn);
        if (modelElements) {
          dbIds = await this.getLeafNodes(model, dbIds);

          Object.entries(modelElements.dbIds).forEach(([cadId, dbId]) => {
            if (dbIds?.includes(dbId as number)) {
              cadIds.add(cadId);
            }
          });
        }
      };
    } catch (e) {
      console.log(e);
    }
    return modelToCadIdMap;
  };

  public getCadIdsFromIsolations = async (isolations: AggregateIsolation[] | null | undefined): Promise<Map<string, Set<string>>> => {
    const getOrAddModel = (modelElements: Map<string, Set<string>>, modelId: string): Set<string> => {
      if (!modelElements.has(modelId)) {
        modelElements.set(modelId, new Set<string>());
      }
      return modelElements.get(modelId) as Set<string>;
    };

    const modelToCadIdMap = new Map<string, Set<string>>();

    try {
      for await (const isolation of isolations || []) {
        const model = isolation.model;
        let dbIds = isolation.ids;
        // @ts-ignore __modelElements is a custom property
        const modelElements: any = model.__modelElements as ModelElements;
        const cadIds = getOrAddModel(modelToCadIdMap, model.myData?.urn);
        if (modelElements) {
          dbIds = await this.getLeafNodes(model, dbIds);

          Object.entries(modelElements.dbIds).forEach(([cadId, dbId]) => {
            if (dbIds?.includes(dbId as number)) {
              cadIds.add(cadId);
            }
          });
        }
      }
    } catch (e) {
      console.log(e);
    }
    return modelToCadIdMap;
  };

  public search = async (array: any, keyword: string): Promise<any> => {
    const regExp = new RegExp(keyword, "gi");
    const check = (obj: any) => {
      if (obj !== null && typeof obj === "object") {
        return Object.values(obj).some(check);
      }
      if (Array.isArray(obj)) {
        return obj.some(check);
      }
      return (typeof obj === "string" || typeof obj === "number") && regExp.test(obj as any);
    };
    return array.filter(check);
  };

  public resetView = async (): Promise<void> => {
    console.log(`[${this._id}] Resetting view...`);
    this.viewer?.showAll();
    this.viewer?.getVisibleModels().forEach((model: any) => {
      this.viewer?.isolate(model.getRootId(), model);
      this.viewer?.fitToView(null, model);
    });
  };
}

const errorCodes: { [K: number]: string; } = {
  1: "An unknown failure has occurred.",
  2: "Bad data (corrupted or malformed) was encountered.",
  3: "A network failure was encountered.",
  4: "Access was denied to a network resource (HTTP 403)",
  5: "A network resource could not be found (HTTP 404)",
  6: "A server error was returned when accessing a network resource (HTTP 5xx)",
  7: 'An unhandled response code was returned when accessing a network resource (HTTP "everything else")',
  8: "Browser error: webGL is not supported by the current browser",
  9: "There is nothing viewable in the fetched document",
  10: "Browser error: webGL is supported, but not enabled",
  11: "There is no geometry in loaded model",
  12: "Collaboration server error",
};
